import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import AppDatePicker from 'components/dateTime/AppDatePicker';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import HistoryDeadlineTable from './HistoryDeadlineTable';
import { COMMENT_MAX_LENGTH, type HistoryDeadlineFormValues, type HistoryDeadlineRow } from './model';

interface HistoryDeadlineFormProps {
    values: HistoryDeadlineFormValues;
    setFieldValue: (field: string, value: unknown) => void;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    deadlineError?: string;
    commentError?: string;
    history: HistoryDeadlineRow[];
    historyLoading: boolean;
    autofocus?: boolean;
}

const HistoryDeadlineForm = ({
    values,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    deadlineError,
    commentError,
    history,
    historyLoading,
    autofocus = true,
}: HistoryDeadlineFormProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const refInput = useRef<HTMLTextAreaElement>(null);
    const comment = String(values.comment || '');
    const canSave = Boolean(values.deadline) && Boolean(comment.trim()) && !isSubmitting;

    useEffect(() => {
        if (!autofocus) return;
        setTimeout(() => refInput.current?.focus(), 100);
    }, [autofocus]);

    return (
        <Form className="d-flex flex-column h-100" onSubmit={handleSubmit}>
            <div className="off-create-team-form d-flex flex-column h-100">
                <div className="off-create-team-content px-3">
                    <Form.Group className="mb-3">
                        <label
                            className="form-label text-muted"
                            htmlFor="history-deadline-input"
                        >
                            {t('historyDeadline.labelNewDate')}
                        </label>
                        <AppDatePicker
                            id="history-deadline-input"
                            value={values.deadline}
                            onChange={(iso) => setFieldValue('deadline', iso)}
                        />
                        {deadlineError ? (
                            <div className="invalid-feedback d-block">{deadlineError}</div>
                        ) : null}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted" htmlFor="history-deadline-comment">
                            {t('historyDeadline.labelComment')}
                        </label>
                        <Form.Control
                            as="textarea"
                            id="history-deadline-comment"
                            ref={refInput}
                            rows={3}
                            value={comment}
                            maxLength={COMMENT_MAX_LENGTH}
                            onChange={(e) =>
                                setFieldValue(
                                    'comment',
                                    e.target.value.slice(0, COMMENT_MAX_LENGTH),
                                )
                            }
                        />
                        {commentError ? (
                            <div className="invalid-feedback d-block">{commentError}</div>
                        ) : null}
                        <div className="form-text text-end">
                            {comment.length} / {COMMENT_MAX_LENGTH}
                        </div>
                    </Form.Group>

                    <div className="mb-2">
                        <div className="text-muted small mb-2">{t('historyDeadline.historyTitle')}</div>
                        <HistoryDeadlineTable rows={history} loading={historyLoading} />
                    </div>
                </div>

                <div className="p-3 offcanvas-footer off-create-team-footer">
                    <div className="d-flex justify-content-end">
                        <Button
                            type="submit"
                            variant="info"
                            disabled={!canSave}
                            className="min100px"
                        >
                            {spinnerTitle(
                                <>
                                    <SvgSave className="me-2 mtop-2" />
                                    {t('common:buttons.save')}
                                </>,
                                isSubmitting,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default HistoryDeadlineForm;
